import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { ShieldAlert, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../stores/authStore'
import { parseStoreNames, canAccessStore } from '../utils/storeAccess'

/**
 * Sincroniza a URL /editor/:siteId com a loja ativa da sessão.
 *
 * - :siteId permitido e diferente da loja ativa → troca automática (switchStore),
 *   corrigindo o antigo bug de "mudar a URL na mão buga o editor".
 * - :siteId não permitido → tela de "sem acesso" com botão para a loja ativa.
 */
export default function StoreRouteGuard({ children }: { children: React.ReactNode }) {
  const { siteId } = useParams<{ siteId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, switchStore } = useAuthStore()
  const [isSwitching, setIsSwitching] = useState(false)
  const switchingRef = useRef(false)

  const activeStore = user?.storeName?.toLowerCase() || ''
  const urlStore = siteId?.toLowerCase() || ''

  // Fallback para sessões antigas sem allowedStores: mono-loja usa o próprio storeName
  const allowedStores = user?.allowedStores?.length
    ? user.allowedStores
    : parseStoreNames(user?.storeName)
  const isAllowed =
    !!urlStore && (user?.isMultiStore || canAccessStore(allowedStores, urlStore))
  const needsSwitch = isAllowed && urlStore !== activeStore

  useEffect(() => {
    if (!needsSwitch || switchingRef.current) return
    switchingRef.current = true
    setIsSwitching(true)

    switchStore(urlStore)
      .then(() => {
        queryClient.clear()
      })
      .catch((error: any) => {
        toast.error(error?.message || `Erro ao trocar para a loja '${urlStore}'.`)
        navigate(`/editor/${activeStore}/home`, { replace: true })
      })
      .finally(() => {
        switchingRef.current = false
        setIsSwitching(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSwitch, urlStore])

  if (!isAllowed) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-73px)] bg-gray-50 px-6 text-center">
        <ShieldAlert className="w-14 h-14 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Sem acesso à loja &apos;{siteId}&apos;
        </h2>
        <p className="text-gray-500 mb-6 max-w-md">
          Seu usuário não tem permissão para acessar esta loja. Se você acredita
          que isso é um erro, fale com o administrador do sistema.
        </p>
        <button
          onClick={() => navigate(`/editor/${activeStore}/home`)}
          className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
        >
          Voltar para a loja {activeStore}
        </button>
      </div>
    )
  }

  if (needsSwitch || isSwitching) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-73px)] bg-gray-50">
        <Loader2 className="w-10 h-10 text-primary-500 animate-spin mb-4" />
        <p className="text-gray-600">Trocando para a loja &apos;{urlStore}&apos;...</p>
      </div>
    )
  }

  return <>{children}</>
}
